import { onUnmounted } from 'vue';

/**
 * Re-enables touch scrolling inside a position:fixed offcanvas/bottom-sheet
 * after the page is backgrounded (iOS lock screen) or restored from the BFCache,
 * where Safari tends to freeze momentum scrolling on fixed overflow containers.
 *
 * Toggling overflow forces a reflow so the scroll area becomes touch-scrollable
 * again. Listeners are auto-removed when the component unmounts.
 *
 * Call from mounted() (same pattern as usePOSSessionSocket):
 *   useScrollRestore(() => this.$refs.scrollArea)
 *
 * @param {Function} getEl  returns the scrollable element (or null/undefined)
 */
export function useScrollRestore(getEl) {
  const restore = () => {
    if (document.visibilityState !== 'visible') return;
    requestAnimationFrame(() => {
      const el = getEl?.();
      if (!el) return;
      const prev = el.style.overflowY;
      el.style.overflowY = 'hidden';
      void el.offsetHeight; // force reflow
      el.style.overflowY = prev || 'auto';
    });
  };

  document.addEventListener('visibilitychange', restore);
  window.addEventListener('pageshow', restore);

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', restore);
    window.removeEventListener('pageshow', restore);
  });

  return { restore };
}
